
import * as React from 'react';
import { FileExtension, FileRecord, FileReferenceType, NonVideoFileExtensions } from '../../amorg-common';
import { deleteReferenceFile, getReferenceFiles, uploadReferenceFile } from '../../api/filesClientApi';
import { FileUploadButton } from '../formField/FileUploadButton';
import { Flex } from './FlexHelpers';
import { Card } from '../Card';

import { FileExtensionIcon } from '../FileExtensionIcon';
import { FileLinkWithTokenRefresh } from '../FileLinkWithTokenRefresh';
import { DeleteConfirmationButton } from './DeleteConfirmationButton';
import { Small } from '../typography/Body';
import { Label } from '../Label';
import { LoadingPageOverlay } from '../LoadingPageOverlay';
import { MessageCard } from '../Message';
import { LinkButton } from '../button/LinkButton';
import { Divider } from '../Divider';
import { colors } from '../colors/color';
import { TextAvatar } from '../avatar/TextAvatar';
import { CollapsiblePanelHeader } from '../collapsible/CollapsiblePanelHeader';
import { AnimatedCollapsibleContent } from '../collapsible/AnimatedCollapsibleContent';

type ReferenceFilesPanelCollapsibleProps = {
    objectId:string,
    referenceType:FileReferenceType,
    onGetFilesCount?: (count:number) => void
}

export function ReferenceFilesPanelCollapsible(props:ReferenceFilesPanelCollapsibleProps):JSX.Element
{
    const [isOpen, setIsOpen] = React.useState<boolean>(false);
    const [filesCount, setFilesCount] = React.useState<number>(0);

    return (
        <Flex flexDirection="column" gap={4}>
            <CollapsiblePanelHeader
                isOpen={isOpen}
                onChangeOpenState={setIsOpen}
                closedOrDefaultHeader={
                    <Flex gap={8} alignItems='center'>
                        <Label>Vedhæftede filer</Label>
                        <TextAvatar size="20px" text={filesCount.toString()}
                            backgroundColor={filesCount > 0 ? colors.avatar.background : colors.avatar.backgroundMuted} />
                    </Flex>
                }
            />
            <AnimatedCollapsibleContent open={isOpen} eagerRender>
                <Card variant="outlined">
                    <ReferenceFilesPanel 
                        objectId={props.objectId} 
                        referenceType={props.referenceType} 
                        onGetFilesCount={
                            (count) => {
                                setIsOpen(count > 0);
                                setFilesCount(count);
                                props.onGetFilesCount?.(count);
                            }
                        } 
                    />
                </Card>
            </AnimatedCollapsibleContent>
        </Flex>
    );
}

type ReferenceFilesPanelProps = {
    objectId:string,
    referenceType:FileReferenceType,
    hide?:boolean,
    onGetFilesCount?: (count:number) => void
}


export function ReferenceFilesPanel(props:ReferenceFilesPanelProps):JSX.Element
{
    const [loading, setLoading] = React.useState<boolean>(false);
    const [files, setFiles] = React.useState<FileRecord[]>([]);
    const [error, setError] = React.useState<string>('');
    
    React.useEffect(() => {
        getReferenceFiles({ type: props.referenceType, id: props.objectId })
        .then(fetchedFiles => {
                setFiles(fetchedFiles);
                props.onGetFilesCount?.(fetchedFiles.length);
                setLoading(false);
            });
    }, [props.objectId, props.referenceType]);

    
    async function handleFileUpload(file:File)
    {
        setLoading(true);
        const result = await uploadReferenceFile({ type: props.referenceType, id: props.objectId }, file);
        if(!result.isSuccess)
        {
            if(result.errorMessage === 'uploadReferenceFile_empty_file')
                setError('Filen er tom. Vælg en gyldig fil.');

            else if(result.errorMessage === 'uploadReferenceFile_file_too_big')
                setError('Filen er for stor. Vælg en fil på maks. 50 MB.');

            else if(result.errorMessage === 'uploadReferenceFile_invalid_public_file_name')
                setError('Ugyldigt filnavn. Vælg en gyldig fil.');

            else if(result.errorMessage === 'uploadReferenceFile_public_file_name_in_use')
                setError('Filnavnet er allerede i brug.');

            else if(result.errorMessage === 'uploadReferenceFile_invalid_file_extension')
                setError('Filtypen er ikke understøttet. Vælg en gyldig fil.');

            else {
                setError('Der opstod en fejl. Prøv igen.');
            }

            setLoading(false);
        }
        else {
            setError('');
            const updatedFiles = await getReferenceFiles({ type: props.referenceType, id: props.objectId });
            setFiles(updatedFiles);
            setLoading(false);
            props.onGetFilesCount?.(updatedFiles.length);
        }
    }

    async function handleFileDelete(privateFileName:string)
    {
        setLoading(true);
        const result = await deleteReferenceFile(privateFileName);
        if(!result.isSuccess)
        {
            setError('Der opstod en fejl ved sletning af filen. Prøv igen.');
            setLoading(false);
            return;
        }
        else{
            const updatedFiles = await getReferenceFiles({ type: props.referenceType, id: props.objectId });
            setFiles(updatedFiles);
            setLoading(false);
            props.onGetFilesCount?.(updatedFiles.length);
        }
    }

    if(props.hide)
        return <></>;
    
    return (        
            <Flex flexDirection="column" justifyContent='stretch' grow='1' gap={8} style={{ minWidth:0 }}>
            
                <LoadingPageOverlay loading={loading} />
            
                <div style={{ flexGrow: 1 }}>
                    {files.map(file => (
                        <Flex 
                            flexDirection='row' 
                            alignItems='center' 
                            justifyContent="space-between" 
                            gap={16}
                            key={file.publicFileName}
                            style={{ minWidth: 0 }}
                        >
                            
                            <Flex flexDirection='row' alignItems='center' gap={8} 
                                style={{ flex: 1, minWidth:0 }}>

                                
                                <FileExtensionIcon filename={file.publicFileName} />
                                
                                
                                <FileLinkWithTokenRefresh 
                                    publicFileName={file.publicFileName} 
                                    privateFileName={file.privateFileName} 
                                    style={{
                                        fontSize: '14px',
                                        overflow: 'hidden', 
                                        textOverflow: 'ellipsis', 
                                        whiteSpace: 'nowrap'
                                    }}
                                />
                                
                                <span style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>
                                    ({(file.sizeBytes / 1024).toFixed(2)} KB)
                                </span>
                            </Flex>

                            <Flex 
                                flexDirection='row' 
                                alignItems='center' 
                                gap={8}
                                style={{ flexShrink: 0 }}
                            >
                                <LinkButton
                                    variant='text'
                                    text=''
                                    icon='interface_actions_download'
                                    href={'/files/' + file.privateFileName}
                                    download={file.publicFileName}
                                />
                                <DeleteConfirmationButton
                                    buttonText=''
                                    tooltipHorizontalAlignment='right'
                                    tooltipText='Slet fil'
                                    variant='destructive-text'
                                    requireKey={false}
                                    confirmationHeading='Slet fil'
                                    confirmationText={`Er du sikker på at du vil slette filen "${file.publicFileName}"?`}
                                    onConfirmDelete={async () =>
                                    {
                                        await handleFileDelete(file.privateFileName);
                                    }}    
                                />
                            </Flex>
                            
                        </Flex>
                    ))}

                    {files.length === 0 && (
                        <Small>Ingen filer vedhæftet</Small>
                    )}

                    {error && (
                        <Flex flexDirection="row" justifyContent="center" alignItems="center">
                            <MessageCard variant='error' grow={1}>{error}</MessageCard>
                        </Flex>
                    )}
                </div>


                {files.length > 0 &&   
                    <Divider variant="light" />
                }

                <Flex justifyContent="flex-end">
                    <FileUploadButton
                        inputName='uploaded_file'
                        fileTypes={NonVideoFileExtensions as unknown as FileExtension[]}
                        onClickUpload={handleFileUpload} 
                        text='Upload fil'
                        />
                </Flex>
            </Flex>

    );
}